// Ideas & bugs board — the public "Pomysły i błędy" wall where users submit,
// vote, and comment; the most-voted items get built first. INTENT: give users a
// visible voice and a reason to come back, and give us a demand-ranked backlog.
//
// For now this is LOCAL-ONLY: a mock seed of community items plus whatever the
// user adds/votes/comments, persisted in one KV blob. The real shared backend
// (app_idea / app_idea_vote / app_idea_comment) lands at Etap 2 (deploy 1.52);
// the store API is shaped so swapping the persistence layer won't touch screens.

import { create } from 'zustand';

import {
  boardApiAvailable,
  commentIdeaApi,
  createIdeaApi,
  fetchIdeaDetailApi,
  fetchIdeasApi,
  voteIdeaApi,
} from '@/api/board-api';
import { getDb } from '@/db';
import { uuid } from '@/utils/format';

const KV_IDEAS = 'ideas_board';
// Ids of ideas submitted from THIS device — the board is community-wide, so a
// "Twoje" badge is the only way to find your own entries again. Kept locally
// (works in both backend and mock mode, no server change needed).
const KV_MY_IDEAS = 'ideas_board_mine';

export type IdeaKind = 'IDEA' | 'BUG';
export type IdeaStatus = 'NEW' | 'IN_PROGRESS' | 'DONE';

export interface IdeaComment {
  id: string;
  author: string;
  text: string;
  createdAt: string;
}

export interface Idea {
  id: string;
  kind: IdeaKind;
  title: string;
  description: string;
  status: IdeaStatus;
  votes: number;
  votedByMe: boolean;
  createdAt: string;
  comments: IdeaComment[];
}

// Seed mirrors the mock (tablica_pomysłów): three community items across the
// three statuses so the board never looks empty on first open.
const SEED: Idea[] = [
  {
    id: 'seed-dark-mode',
    kind: 'IDEA',
    title: 'Ciemny motyw (Dark Mode)',
    description:
      'Dodanie trybu nocnego dla całej aplikacji. Ułatwi to korzystanie z aplikacji wieczorami i zmniejszy zużycie baterii w telefonach z ekranami OLED.',
    status: 'IN_PROGRESS',
    votes: 42,
    votedByMe: false,
    createdAt: '2026-06-28T10:15:00.000Z',
    comments: [
      { id: 'c1', author: 'Wojtek K.', text: 'Super pomysł, bardzo na to czekam!', createdAt: '2026-07-11T10:15:00.000Z' },
      { id: 'c2', author: 'Ania M.', text: 'Właśnie miałam to samo zgłosić. +1', createdAt: '2026-07-10T18:20:00.000Z' },
    ],
  },
  {
    id: 'seed-budget-chart-bug',
    kind: 'BUG',
    title: 'Błąd w wykresie budżetu',
    description: 'Na ekranie podsumowania linia planu się ucina.',
    status: 'NEW',
    votes: 15,
    votedByMe: false,
    createdAt: '2026-07-05T09:00:00.000Z',
    comments: [],
  },
  {
    id: 'seed-pdf-export',
    kind: 'IDEA',
    title: 'Eksport do PDF',
    description: 'Możliwość pobrania raportu miesięcznego.',
    status: 'DONE',
    votes: 128,
    votedByMe: false,
    createdAt: '2026-05-20T09:00:00.000Z',
    comments: [],
  },
];

interface IdeasState {
  hydrated: boolean;
  ideas: Idea[];
  /** Ids of ideas submitted from this device (drives the "Twoje" badge). */
  myIdeaIds: string[];
  hydrate: () => Promise<void>;
  /** Backend-mode only: refresh one item's comments/votes from the server. */
  loadDetail: (id: string) => Promise<void>;
  addIdea: (input: { kind: IdeaKind; title: string; description: string }) => Promise<Idea>;
  toggleVote: (id: string) => Promise<void>;
  addComment: (id: string, text: string, author: string) => Promise<void>;
}

// Local persistence is the mock/offline path only. When a backend URL is set the
// server is the source of truth and we don't cache to kv.
async function persist(ideas: Idea[]): Promise<void> {
  const db = await getDb();
  await db.setKv(KV_IDEAS, JSON.stringify(ideas));
}

async function persistMine(ids: string[]): Promise<void> {
  const db = await getDb();
  await db.setKv(KV_MY_IDEAS, JSON.stringify(ids));
}

export const useIdeasStore = create<IdeasState>((set, get) => ({
  hydrated: false,
  ideas: SEED,
  myIdeaIds: [],

  hydrate: async () => {
    // Own-submission ids are device-local in BOTH modes.
    const db = await getDb();
    const rawMine = await db.getKv(KV_MY_IDEAS);
    if (rawMine) set({ myIdeaIds: JSON.parse(rawMine) as string[] });
    // Backend first; on any failure fall back to the local seed/cache so the
    // board is never empty and works offline.
    if (boardApiAvailable()) {
      const remote = await fetchIdeasApi();
      if (remote) {
        set({ hydrated: true, ideas: remote });
        return;
      }
    }
    const raw = await db.getKv(KV_IDEAS);
    set({ hydrated: true, ideas: raw ? (JSON.parse(raw) as Idea[]) : SEED });
  },

  loadDetail: async (id) => {
    if (!boardApiAvailable()) return; // local mode already holds comments
    const detail = await fetchIdeaDetailApi(id);
    if (!detail) return;
    set((s) => ({ ideas: s.ideas.map((i) => (i.id === id ? detail : i)) }));
  },

  addIdea: async ({ kind, title, description }) => {
    if (boardApiAvailable()) {
      const created = await createIdeaApi({ kind, title, description });
      if (created) {
        const myIdeaIds = [...get().myIdeaIds, created.id];
        await persistMine(myIdeaIds);
        set((s) => ({ ideas: [created, ...s.ideas], myIdeaIds }));
        return created;
      }
    }
    const now = new Date().toISOString();
    const idea: Idea = {
      id: uuid(),
      kind,
      title,
      description,
      status: 'NEW',
      votes: 1,
      votedByMe: true, // your own submission counts as your vote
      createdAt: now,
      comments: [],
    };
    const ideas = [idea, ...get().ideas];
    const myIdeaIds = [...get().myIdeaIds, idea.id];
    await persist(ideas);
    await persistMine(myIdeaIds);
    set({ ideas, myIdeaIds });
    return idea;
  },

  toggleVote: async (id) => {
    if (boardApiAvailable()) {
      const result = await voteIdeaApi(id);
      if (result) {
        set((s) => ({
          ideas: s.ideas.map((i) =>
            i.id === id ? { ...i, votes: result.votes, votedByMe: result.votedByMe } : i,
          ),
        }));
        return;
      }
    }
    const ideas = get().ideas.map((i) =>
      i.id === id ? { ...i, votedByMe: !i.votedByMe, votes: i.votes + (i.votedByMe ? -1 : 1) } : i,
    );
    await persist(ideas);
    set({ ideas });
  },

  addComment: async (id, text, author) => {
    if (boardApiAvailable()) {
      const created = await commentIdeaApi(id, text, author);
      if (created) {
        set((s) => ({
          ideas: s.ideas.map((i) => (i.id === id ? { ...i, comments: [...i.comments, created] } : i)),
        }));
        return;
      }
    }
    const comment: IdeaComment = { id: uuid(), author, text, createdAt: new Date().toISOString() };
    const ideas = get().ideas.map((i) =>
      i.id === id ? { ...i, comments: [...i.comments, comment] } : i,
    );
    await persist(ideas);
    set({ ideas });
  },
}));
